import { guards } from '@shared/helpers/typeguards.helpers';
import React, {
  createContext,
  CSSProperties,
  ReactElement,
  useContext,
} from 'react';
import { useGetScalingSize } from '../report-style.helpers';
import {
  figmaPxToWidthPercent,
  REPORT_BORDER_WIDTH_PERCENT,
} from '../report.constants';
import { makeStyles } from 'tss-react/mui';

/**
 * Table gaps. Number for both axes, array for [row, column].
 */
export type ReportTableGaps = number | [rowGap: number, columnGap: number];
const parseTableGaps = (
  tableGaps: ReportTableGaps,
): { row: number; column: number } => {
  return guards.is.number(tableGaps)
    ? { row: tableGaps, column: tableGaps }
    : { row: tableGaps[0], column: tableGaps[1] };
};

const ReportTableContext = createContext<{
  rowGap: number;
  columnGap: number;
  templateColumns: CSSProperties['gridTemplateColumns'];
}>({
  rowGap: 0,
  columnGap: 0,
  templateColumns: '1fr',
});

type NestedReactElements = ReactElement<any> | NestedReactElements[];

const useStyles = makeStyles<{
  rowGap: number;
  getScalingSize: (value: number) => string;
  rowDelimiters: boolean;
}>()((theme, { rowGap, getScalingSize, rowDelimiters }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: getScalingSize(rowDelimiters ? rowGap / 2 : rowGap),
    '& > *:not(:last-child):after': {
      content: '""',
      marginTop: `calc(${getScalingSize(rowGap / 2)} - ${getScalingSize(figmaPxToWidthPercent(0.5))})`,
      marginBottom: `-${getScalingSize(figmaPxToWidthPercent(0.5))}`,
      display: rowDelimiters ? 'block' : 'none',
      borderBottom: `${getScalingSize(REPORT_BORDER_WIDTH_PERCENT)} solid ${theme.palette.divider}`,
    },
  },
}));

const ReportTableRoot: React.FC<{
  tableGaps?: ReportTableGaps;
  templateColumns: CSSProperties['gridTemplateColumns'];
  children: NestedReactElements;
  rowDelimiters?: boolean;
}> = ({ children, tableGaps = 0, templateColumns, rowDelimiters = false }) => {
  const getScalingSize = useGetScalingSize();
  const gaps = parseTableGaps(tableGaps);
  const { classes } = useStyles({
    rowDelimiters,
    rowGap: gaps.row,
    getScalingSize,
  });

  return (
    <ReportTableContext.Provider
      value={{ rowGap: gaps.row, columnGap: gaps.column, templateColumns }}
    >
      <div className={classes.root}>{children}</div>
    </ReportTableContext.Provider>
  );
};

const ReportTableRow: React.FC<{
  children: NestedReactElements;
  alignCells?: CSSProperties['alignItems'];
}> = ({ children, alignCells = 'center' }) => {
  const { templateColumns, columnGap } = useContext(ReportTableContext);
  const getScalingSize = useGetScalingSize();
  return (
    <div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: templateColumns,
          gap: getScalingSize(columnGap),
          alignItems: alignCells,
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const ReportTable = Object.assign(ReportTableRoot, {
  Row: ReportTableRow,
});
